import moment from 'moment';

import { TECHNICIAN_CHANGE_VALUES } from '@shared/store/constants/mutationTypes';

import emailValidation from '@shared/lib/errorCatch/emailValidation';
import phoneProTechValidation from '@shared/lib/errorCatch/phoneProTechValidation';
import apiClient from '@shared/lib/general/apiClient';

const formTechnicianUrl = (filters, baseUrl) => {
  let url = baseUrl;
  filters.forEach((filter) => {
    const { active, key } = filter;
    if (active) {
      const {
        activeOption: { value },
      } = filter;
      if (!url.includes('?')) {
        url += '?';
      } else {
        url += '&';
      }
      if (key === 'techs') {
        url += `technicianId=${value}`;
      }
      if (key === 'status' && value !== 'all') {
        url += `status=${value}`;
      }
      if (key === 'branch') {
        url = url.replace('{corpId}', value);
      }
      if (key === 'installDate') {
        let start = null;
        const end = moment().format('YYYY-MM-DD');
        if (value === 'lastWeek') {
          start = moment().subtract(6, 'days').format('YYYY-MM-DD');
        } else if (value === 'lastMonth') {
          start = moment().subtract(29, 'days').format('YYYY-MM-DD');
        } else if (value === 'lastYear') {
          start = moment().subtract(364, 'days').format('YYYY-MM-DD');
        }
        url += `start=${start}&end=${end}`;
      }
      if (key === 'myTech' && value !== 'any') {
        url += `isMyTech=${value}`;
      }
    }
  });
  return url;
};

const actions = {
  changeTechnicianValues({ commit }, changes) {
    commit({
      type: TECHNICIAN_CHANGE_VALUES,
      changes,
    });
  },
  changeTechnicianValue({ dispatch }, change) {
    dispatch('changeTechnicianValues', [change]);
  },
  async getTechniciansForFilter({ dispatch, state, rootState }, technicianId) {
    const { filters } = state;
    const {
      admin: {
        adminFilter: { corpId },
      },
    } = rootState;
    try {
      const { data } = await apiClient({
        method: 'GET',
        url: `/technicians/all/${corpId}`,
      });
      const newFilters = filters.map((filter) => {
        const newFilter = {
          ...filter,
        };
        if (filter.key === 'techs') {
          let activeOption = {};
          const techOptions = data.map((tech) => {
            const option = {
              label: `${tech.firstName} ${tech.lastName}`,
              value: tech.id,
            };
            if (technicianId && Number(tech.id) === Number(technicianId)) {
              activeOption = option;
            }
            return option;
          });
          newFilter.options = techOptions.sort((a, b) => {
            const aLowerCase = a.label.toLowerCase();
            const bLowerCase = b.label.toLowerCase();
            if (aLowerCase > bLowerCase) {
              return 1;
            }
            if (aLowerCase < bLowerCase) {
              return -1;
            }
            return 0;
          });
          if (activeOption.value) {
            newFilter.activeOption = activeOption;
            newFilter.active = true;
          }
        }
        return newFilter;
      });
      dispatch('changeTechnicianValue', {
        field: 'filters',
        value: newFilters,
      });
    } catch (err) {
      console.error(err);
    }
  },
  async exportTechnicianList({ state, rootState }) {
    const { filters } = state;
    const {
      admin: {
        adminFilter: { corpId },
      },
    } = rootState;
    try {
      let url = formTechnicianUrl(filters, '/technicians/export/all/{corpId}');
      if (url.includes('{corpId}')) {
        url = url.replace('{corpId}', corpId);
      }
      const { data } = await apiClient({
        method: 'GET',
        url,
      });
      return data;
    } catch (err) {
      console.error(err);
    }
    return false;
  },
  async getPartnerSettings({ dispatch, rootState }) {
    const {
      admin: {
        adminFilter: { corpId },
      },
    } = rootState;
    const { data: partnerSettings } = await apiClient({
      method: 'GET',
      url: `/partner/settings/${Math.abs(corpId)}`,
    });
    dispatch('changeTechnicianValues', [
      {
        field: 'partnerSettings',
        value: partnerSettings,
      },
    ]);
  },
  async getTechnicianList({ dispatch, state, rootState, getters }) {
    const { filters, allTechnicians } = state;
    const {
      admin: {
        adminFilter: { corpId },
      },
    } = rootState;

    const { isPendingActive } = getters;

    let techs = [];
    try {
      let url = formTechnicianUrl(filters, '/technicians/all/{corpId}');
      if (url.includes('{corpId}')) {
        url = url.replace('{corpId}', corpId);
      }
      const { data } = await apiClient({
        method: 'GET',
        url,
      });

      techs = data;
      const statusFilterActive = filters.find((f) => f.key === 'status')?.active;
      if (isPendingActive) {
        techs = techs.filter((item) => item.lastInvite && item.createPassword === true);
      } else if (statusFilterActive) {
        techs = techs.filter((item) => item.createPassword === false);
      }

      const installFilterActive = filters.find((f) => f.key === 'installDate')?.active;
      if (installFilterActive) {
        techs = techs.filter((item) => item.unitCount > 0);
      }
      const changes = [
        {
          field: 'technicians',
          value: techs,
        },
      ];
      if (allTechnicians.length < techs.length) {
        changes.push({
          field: 'allTechnicians',
          value: techs,
        });
      }

      dispatch('changeTechnicianValues', changes);
    } catch (err) {
      console.error(err);
    }
  },
  async declineTech({ dispatch, state, rootState }) {
    const { selectedTech, technicians } = state;
    const {
      admin: {
        adminFilter: { corpId },
      },
    } = rootState;

    try {
      await apiClient({
        method: 'PUT',
        url: '/technicians/decline/profile/changes',
        data: selectedTech,
      });

      const newTechnicians = technicians
        .map((tech) => {
          let newTech = tech;
          if (tech.id === selectedTech.id) {
            newTech = {
              ...tech,
              pendingProfileChanges: {
                approvalPending: false,
                profileUrl: null,
              },
            };
          }
          return newTech;
        })
        .filter((tech) => tech.corporationId === corpId);

      dispatch('changeTechnicianValues', [
        {
          field: 'technicians',
          value: newTechnicians,
        },
        {
          field: 'selectedTech',
          value: {
            ...selectedTech,
            pendingProfileChanges: {
              approvalPending: false,
              profileUrl: null,
            },
          },
        },
      ]);
      dispatch('showSuccessToast', {
        header: 'Declined technician photo.',
      });
    } catch (err) {
      dispatch('showAlertToast', {
        header: 'There was an unexpected error.',
        message: 'Please try again or contact support.',
      });
    }
  },
  async updateTechnician({ dispatch, state, rootState }, payload) {
    const { technicians, selectedTech, imageForm } = state;
    const {
      admin: {
        adminFilter: { corpId },
      },
    } = rootState;

    const { updateTech } = payload;

    try {
      const { firstName, lastName } = updateTech;
      await apiClient({
        method: 'PUT',
        url: `/technicians/update/${updateTech.id}`,
        data: updateTech,
      });
      const updatedTech = {
        ...updateTech,
      };
      if (imageForm && imageForm.get('photo')) {
        imageForm.append('techId', updateTech.id);
        const { data: imageUrl } = await apiClient({
          url: '/technicians/upload/profile/image',
          method: 'POST',
          data: imageForm,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        updatedTech.profileUrl = imageUrl;
      }

      const newTechnicians = technicians
        .map((tech) => {
          let newTech = tech;
          if (tech.id === updatedTech.id) {
            newTech = {
              ...tech,
              ...updatedTech,
            };
          }
          return newTech;
        })
        .filter((tech) => tech.corporationId === corpId);

      dispatch('changeTechnicianValues', [
        {
          field: 'technicians',
          value: newTechnicians,
        },
        {
          field: 'selectedTech',
          value: { ...selectedTech, ...updatedTech },
        },
      ]);
      dispatch('showSuccessToast', {
        header: 'Technician information saved.',
        message: `${firstName} ${lastName} has been edited.`,
      });
    } catch (err) {
      console.error(err);
      dispatch('showAlertToast', {
        header: 'There was an unexpected error.',
        message: 'Please try again or contact support.',
      });
      throw err;
    }
  },
  async deactivateTech({ dispatch, state }) {
    const { filters, technicians, selectedTech } = state;
    try {
      const { id, firstName, lastName } = selectedTech;
      await apiClient({
        method: 'PUT',
        url: `/technicians/disable/${id}`,
        data: selectedTech,
      });
      const { activeOption } = filters.find((f) => f.key === 'status');
      let newTechnicians = technicians.slice();
      if (activeOption && activeOption.value === 'active') {
        newTechnicians = technicians.filter((tech) => tech.id !== id);
      } else {
        newTechnicians = technicians.map((tech) => {
          let newTech = tech;
          if (tech.id === id) {
            newTech = {
              ...tech,
              accountStatus: 'Deactivated',
            };
          }
          return newTech;
        });
      }
      dispatch('changeTechnicianValue', {
        field: 'technicians',
        value: newTechnicians,
      });
      dispatch('showSuccessToast', {
        header: 'Technician deactivated.',
        message: `${firstName} ${lastName} has been deactivated.`,
      });
    } catch (err) {
      console.error(err);
      dispatch('showAlertToast', {
        header: 'There was an unexpected error.',
        message: 'Please try again or contact support.',
      });
      throw err;
    }
  },
  async reactivateTech({ dispatch, state }) {
    const { filters, technicians, selectedTech } = state;
    try {
      const { id, firstName, lastName } = selectedTech;
      await apiClient({
        method: 'PUT',
        url: `/technicians/reactivate/${id}`,
        data: selectedTech,
      });
      const { activeOption } = filters.find((f) => f.key === 'status');
      let newTechnicians = technicians.slice();
      if (activeOption && activeOption.value === 'deactivated') {
        newTechnicians = technicians.filter((tech) => tech.id !== id);
      } else {
        newTechnicians = technicians.map((tech) => {
          let newTech = tech;
          if (tech.id === id) {
            newTech = {
              ...tech,
              accountStatus: 'Active',
            };
          }
          return newTech;
        });
      }
      dispatch('changeTechnicianValue', {
        field: 'technicians',
        value: newTechnicians,
      });
      dispatch('showSuccessToast', {
        header: 'Technician reactivated.',
        message: `${firstName} ${lastName} has been reactivated.`,
      });
    } catch (err) {
      console.error(err);
      dispatch('showAlertToast', {
        header: 'There was an unexpected error.',
        message: 'Please try again or contact support.',
      });
      throw err;
    }
  },
  async inviteTech({ dispatch, state, rootState }, technician) {
    const { technicians, imageForm, allTechnicians } = state;

    const {
      admin: {
        adminFilter: { corpId },
      },
    } = rootState;

    try {
      const { firstName, lastName, corporationId } = technician;

      const { data } = await apiClient({
        method: 'POST',
        url: '/admins/invite/tech',
        data: technician,
      });
      let profileUrl = null;
      if (imageForm && imageForm.get('photo')) {
        imageForm.append('techId', data.id);
        const { data: imageUrl } = await apiClient({
          url: '/technicians/upload/profile/image',
          method: 'POST',
          data: imageForm,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        profileUrl = imageUrl;
      }

      const selectedPartnerCorpId = corpId;
      if (selectedPartnerCorpId === corporationId) {
        const newTechnician = {
          ...data,
          lastInvite: new Date().toISOString(),
          profileUrl,
          unitCount: 0,
        };
        const newTechnicians = [newTechnician, ...technicians];
        const allNewTechnicians = [newTechnician, ...allTechnicians];
        dispatch('changeTechnicianValues', [
          {
            field: 'technicians',
            value: newTechnicians,
          },
          {
            field: 'allTechnicians',
            value: allNewTechnicians,
          },
        ]);
      }

      dispatch('showSuccessToast', {
        header: 'Invitation sent.',
        message: `${firstName} ${lastName} has been invited.`,
      });
      return true;
    } catch (err) {
      dispatch('showAlertToast', {
        message: 'There was an unexpected error',
      });
      throw err;
    }
  },
  async resendTechInvite({ dispatch, state }, technician) {
    const { technicians } = state;
    try {
      await apiClient({
        method: 'POST',
        url: '/admins/resend/invite/tech',
        data: technician,
      });
      const { firstName, lastName } = technician;
      const newTechnicians = technicians.map((tech) => {
        let newTech = tech;
        if (tech.id === technician.id) {
          newTech = {
            ...tech,
            lastInvite: new Date().toISOString(),
          };
        }
        return newTech;
      });
      dispatch('changeTechnicianValue', {
        field: 'technicians',
        value: newTechnicians,
      });
      dispatch('showSuccessToast', {
        header: 'Invitation sent.',
        message: `${firstName} ${lastName} has been invited.`,
      });
    } catch (err) {
      console.error(err);
      dispatch('showAlertToast', {
        message: 'There was an unexpected error',
      });
      throw err;
    }
  },
  async getTechInstallations({ dispatch, state }) {
    const {
      selectedTech: { id, corporationId },
    } = state;
    const { data } = await apiClient({
      method: 'GET',
      url: `/adoption/stat/installations/corp/${corporationId}?technicianId=${id}`,
    });
    dispatch('changeTechnicianValue', {
      field: 'installations',
      value: data,
    });
  },
  async batchTech({ dispatch, rootState }, payload) {
    const { technicianList } = payload;

    const {
      admin: {
        current: { id: adminId },
        adminFilter: { corpId },
      },
    } = rootState;

    const errorObj = {};

    const techMapped = technicianList.map(({ _, ...technician }) => ({
      ...technician,
      adminId,
      corporationId: corpId,
      createPassword: !technician.password,
      nonInstaller: false,
      lastInvite: new Date().toISOString(),
    }));

    const validations = techMapped.map(async (s, idx) => {
      if (s && s.firstName === '') {
        errorObj[`firstName-${idx}`] = 'First name is not valid';
      }
      if (s && s.lastName === '') {
        errorObj[`lastName-${idx}`] = 'Last name is not valid';
      }
      if (s && s.email === '') {
        errorObj[`email-${idx}`] = 'Email is not valid';
      }
      if (s && s.email && !emailValidation(s.email)) {
        errorObj[`email-${idx}`] = 'Email is not valid';
      }
      if (s && s.phone === '') {
        errorObj[`phone-${idx}`] = 'Phone is not valid';
      }
      if (s && s.phone && !phoneProTechValidation(s.phone)) {
        errorObj[`phone-${idx}`] = 'Phone number is not valid';
      }

      try {
        const { data } = await apiClient({
          method: 'GET',
          url: `/technicians/email/${s.email}`,
        });
        if (data.email === s.email) {
          errorObj[`email-${idx}`] = 'User already exists. Please use a different email address.';
        }
      } catch (err) {
        console.error(err);
      }
    });

    await Promise.all(validations);

    dispatch('changeTechnicianValues', [
      {
        field: 'errorObj',
        value: errorObj,
      },
    ]);

    if (Object.keys(errorObj).length === 0) {
      try {
        await apiClient({
          method: 'POST',
          url: '/technicians/batch/create',
          data: techMapped,
        });

        if (techMapped.length > 1) {
          dispatch('showSuccessToast', {
            header: 'Bulk upload complete.',
            message: `${techMapped.length} technicians invited successfully.`,
          });
        } else {
          dispatch('showSuccessToast', {
            header: 'Bulk upload complete.',
            message: `${techMapped.length} technician invited successfully.`,
          });
        }

        return true;
      } catch (err) {
        console.error(err);
        dispatch('showAlertToast', {
          header: 'There was an unexpected error.',
          message: 'Please try again or contact support.',
        });
        throw err;
      }
    }
    return false;
  },
  async getTechnicianById({ dispatch, rootState }, payload) {
    const { technicianId } = payload;
    const {
      admin: {
        current: { corporationId },
      },
    } = rootState;
    try {
      const { data } = await apiClient({
        method: 'GET',
        url: `/technicians/id/${technicianId}`,
      });
      if (corporationId === 1 || data.corporationId === corporationId) {
        dispatch('changeTechnicianValue', {
          field: 'selectedTech',
          value: data,
        });
      }
    } catch (err) {
      console.error(err);
    }
  },
  async parseTechsUpload({ dispatch, rootState }, payload) {
    const { text } = payload;

    const {
      admin: {
        adminFilter: { corpId },
      },
    } = rootState;

    const textSplit = text.split('\n');
    const errorObj = {};
    const uploadErrorArr = [];
    let fIdx = -1;
    let lIdx = -1;
    let phoneIdx = -1;
    let emailIdx = -1;
    let passwordIdx = -1;
    const headers = textSplit.splice(0, 1)[0].split(',');
    headers.forEach((h, idx) => {
      const lowerCase = h.toLowerCase();
      if (lowerCase.includes('firstname')) {
        fIdx = idx;
      } else if (lowerCase.includes('lastname')) {
        lIdx = idx;
      } else if (lowerCase.includes('phone')) {
        phoneIdx = idx;
      } else if (lowerCase.includes('email')) {
        emailIdx = idx;
      } else if (lowerCase.includes('password')) {
        passwordIdx = idx;
      }
    });

    if (fIdx === -1) {
      uploadErrorArr.push('First Name column is missing!');
    }
    if (lIdx === -1) {
      uploadErrorArr.push('Last Name column is missing!');
    }
    if (phoneIdx === -1) {
      uploadErrorArr.push('Phone column is missing!');
    }
    if (emailIdx === -1) {
      uploadErrorArr.push('Email column is missing!');
    }

    if (uploadErrorArr.length === 0) {
      const allTechs = [];
      textSplit.forEach((row, idx) => {
        const info = row.split(',');
        if (info.length > 1) {
          const cleanedInfo = info.map((v) => {
            let value = v;
            if (v.indexOf('\r') !== -1) {
              value = v.substring(0, v.indexOf('\r'));
            }
            return value.trim();
          });
          const firstName = cleanedInfo[fIdx];
          const lastName = cleanedInfo[lIdx];
          const email = cleanedInfo[emailIdx];
          const phone = cleanedInfo[phoneIdx];
          const password = passwordIdx > -1 ? cleanedInfo[passwordIdx] : null;
          if (firstName) {
            if (!firstName) {
              uploadErrorArr.push(`First name is missing on line ${idx + 2}`);
            }
            if (!lastName) {
              uploadErrorArr.push(`Last name is missing on line ${idx + 2}`);
            }
            if (!phone) {
              uploadErrorArr.push(`Phone is missing on line ${idx + 2}`);
            }
            if (!email) {
              uploadErrorArr.push(`Email is not valid on line ${idx + 2}`);
            }
            allTechs.push({
              firstName,
              lastName,
              email,
              phone,
              password,
            });
          }
        }
      });

      if (uploadErrorArr.length > 0) {
        errorObj.uploadErrorArr = uploadErrorArr;

        dispatch('changeTechnicianValues', [
          {
            field: 'errorObj',
            value: errorObj,
          },
          {
            field: 'parsingTechs',
            value: false,
          },
        ]);
      } else if (allTechs.length > 0) {
        dispatch('changeTechnicianValues', [
          {
            field: 'actionModalView',
            value: 'create-tech',
          },
          {
            field: 'newTechsUpload',
            value: allTechs,
          },
          {
            field: 'parsingTechs',
            value: false,
          },
          {
            field: 'errorObj',
            value: errorObj,
          },
        ]);
      }
    } else {
      errorObj.uploadErrorArr = uploadErrorArr;

      dispatch('changeTechnicianValues', [
        {
          field: 'errorObj',
          value: errorObj,
        },
        {
          field: 'parsingTechs',
          value: false,
        },
      ]);
    }
  },
  async resetTechPassword({ dispatch }, payload) {
    const currentUser = JSON.parse(localStorage.currentUser);
    const { tech, adminCreated } = payload;
    try {
      await apiClient({
        method: 'POST',
        url: `/admins/reset/tech/password/${adminCreated}/${currentUser.customerId}`,
        data: tech,
      });
      if (adminCreated) {
        dispatch('showSuccessToast', {
          header: 'Password Updated.',
          message: `The password has been updated for ${tech.firstName} ${tech.lastName}.`,
        });
      } else {
        dispatch('showSuccessToast', {
          header: 'Password Reset.',
          message: `${tech.firstName} ${tech.lastName} has been sent a reset password notification.`,
        });
      }
    } catch (err) {
      dispatch('showAlertToast', {
        header: 'There was an unexpected error.',
        message: 'Please try again or contact support.',
      });
      throw err;
    }
  },
  async getFailReasonsWithTech({ dispatch, state }) {
    const {
      selectedTech: { id, corporationId },
    } = state;
    try {
      if (corporationId) {
        const { data } = await apiClient({
          method: 'GET',
          url: `adoption/stat/reasons/corp/${corporationId}?techId=${id}`,
        });
        return data;
      }
      return [];
    } catch (err) {
      console.error(err);
      dispatch('showAlertToast', {
        message:
          'There was an unexpected error creating this chart. Please try again or contact support',
      });
      throw err;
    }
  },
};

export default actions;
